import React, { useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { getDoc } from 'firebase/firestore';
import { useSession } from '../context/Session';
import { useT } from '../i18n';
import { Icon, Loading, appStoreLink } from '../components/ui';
import { addDays, dateKey, mondayOf } from '../lib/dates';
import { getWeeklySummary } from '../lib/cloud';
import { saveWeeklySummary, weeklySummaryRef } from '../lib/services';

function StatChip({ icon, label, value, color }) {
  return (
    <div className="gm-card" style={{ padding: 14, textAlign: 'center' }}>
      <Icon name={icon} style={{ color }} />
      <div style={{ fontSize: 22, fontWeight: 800, marginTop: 4 }}>{value}</div>
      <div className="gm-tiny gm-muted">{label}</div>
    </div>
  );
}

export default function WeeklySummary() {
  const t = useT();
  const navigate = useNavigate();
  const { openPremium } = useOutletContext() || {};
  const { user } = useSession();
  const [state, setState] = useState('loading');
  const [summary, setSummary] = useState(null);
  // Summarizes the previous completed week (Mon–Sun), like release builds of the app.
  const weekId = dateKey(addDays(mondayOf(new Date()), -7));

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const snap = await getDoc(weeklySummaryRef(user.id, weekId));
        const cached = snap.exists() ? snap.data() : null;
        if (cached && (user.isPremium || cached.unlockedByAd)) {
          if (alive) { setSummary(cached); setState('content'); }
          return;
        }
        if (!user.isPremium) {
          if (alive) setState('locked');
          return;
        }
        if (alive) setState('generating');
        const s = await getWeeklySummary(user.id, weekId, t.lang);
        await saveWeeklySummary(user.id, weekId, s).catch(() => {});
        if (alive) { setSummary(s); setState('content'); }
      } catch (e) {
        if (alive) setState('error');
      }
    })();
    return () => { alive = false; };
  }, [user.id, user.isPremium, weekId, t.lang]);

  const stats = summary?.stats || {};
  return (
    <div className="gm-content gm-content--narrow gm-enter">
      <div className="gm-page-head">
        <button type="button" className="gm-icon-btn" onClick={() => navigate('/home')} title={t('back')}><Icon name="arrow_back" /></button>
        <h1 className="gm-page-title">{t.upper(t('weeklySummaryPageTitle'))}</h1>
      </div>

      {(state === 'loading' || state === 'generating') && (
        <div className="gm-card gm-card--pad-lg" style={{ textAlign: 'center' }}>
          <Loading />
          <p className="gm-muted">{state === 'generating' ? t('weeklySummaryGenerating') : t('weeklySummaryLoading')}</p>
        </div>
      )}

      {state === 'error' && (
        <div className="gm-card gm-card--pad-lg" style={{ textAlign: 'center' }}>
          <Icon name="error" size={40} className="gm-muted" />
          <p style={{ marginTop: 10 }}>{t('weeklySummaryError')}</p>
        </div>
      )}

      {state === 'locked' && (
        <div className="gm-card gm-card--pad-lg" style={{ textAlign: 'center' }}>
          <Icon name="lock" size={44} style={{ color: 'var(--gm-premium-a)' }} />
          <h2 className="gm-card-title" style={{ marginTop: 10 }}>{t('weeklySummaryLockedTitle')}</h2>
          <p className="gm-muted" style={{ marginTop: 6 }}>{t('weeklySummaryWebLocked')}</p>
          <div className="gm-row gm-wrap" style={{ justifyContent: 'center', marginTop: 18 }}>
            <button type="button" className="gm-btn gm-btn--premium" onClick={openPremium}><Icon name="workspace_premium" /> {t('upgradeToPremium')}</button>
            <a className="gm-btn gm-btn--outline" href={appStoreLink()} target="_blank" rel="noreferrer"><Icon name="smartphone" /> {t('getTheApp')}</a>
          </div>
        </div>
      )}

      {state === 'content' && summary && (
        <div className="gm-stack">
          <div className="gm-card gm-card--pad-lg gm-card--flush">
            <div className="gm-stripe"><span style={{ background: 'var(--gm-workout)' }} /><span style={{ background: 'var(--gm-nutrition)' }} /></div>
            <div style={{ padding: 20 }}>
              <div className="gm-tiny gm-muted">{summary.weekStart} — {summary.weekEnd}</div>
              <h2 style={{ fontSize: 20, fontWeight: 800, marginTop: 6, lineHeight: 1.3 }}>{summary.headline}</h2>
            </div>
          </div>
          <div className="gm-grid-3">
            <StatChip icon="fitness_center" color="var(--gm-workout)" label={t('weeklySummaryWorkoutsChipLabel')} value={`${stats.completed_workout_days ?? 0}/${stats.planned_workout_days ?? 0}`} />
            <StatChip icon="restaurant" color="var(--gm-nutrition)" label={t('weeklySummaryNutritionChipLabel')} value={`${stats.nutrition_days_logged ?? 0}/7`} />
            <StatChip icon="local_fire_department" color="var(--gm-flame-b)" label={t('weeklySummaryAvgCaloriesChipLabel')} value={Math.round(stats.avg_calories_kcal ?? 0)} />
          </div>
          <div className="gm-card gm-card--pad-lg">
            <div className="gm-row" style={{ marginBottom: 8 }}><Icon name="fitness_center" style={{ color: 'var(--gm-workout)' }} /><span className="gm-card-title">{t('weeklySummaryProgramSectionTitle')}</span></div>
            <p style={{ lineHeight: 1.6 }}>{summary.programSummary}</p>
          </div>
          <div className="gm-card gm-card--pad-lg">
            <div className="gm-row" style={{ marginBottom: 8 }}><Icon name="restaurant" style={{ color: 'var(--gm-nutrition)' }} /><span className="gm-card-title">{t('weeklySummaryNutritionSectionTitle')}</span></div>
            <p style={{ lineHeight: 1.6 }}>{summary.nutritionSummary}</p>
          </div>
          {(summary.tips || []).length > 0 && (
            <div className="gm-card gm-card--pad-lg">
              <div className="gm-row" style={{ marginBottom: 8 }}><Icon name="lightbulb" style={{ color: 'var(--gm-gold)' }} /><span className="gm-card-title">{t('weeklySummaryTipsTitle')}</span></div>
              <ul style={{ margin: 0, paddingLeft: 20 }} className="gm-stack-sm">
                {summary.tips.map((tip) => <li key={tip} style={{ lineHeight: 1.5 }}>{tip}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
